import { FC, Dispatch, useEffect, useState } from "react";
import { PdfHandler } from "../../../middleware/handlers/pdf-handler";
import { ViewerToolbarButton } from "./viewer-toolbar";
import type { PdfPointerMode } from "../../../core/pdf/pdf-scene";
import { NormalizeSheetNumber } from "../../../core/pdf/pdf-sheets";

interface PdfToolbarProps {
  pageNumber: number;
  pageCount: number;
  scale: number;
  sheetNumbers: { [pageNumber: number]: string };
  pdfPointerMode: PdfPointerMode;
  onPointerModeChange: (pointerMode: PdfPointerMode) => void;
  // left out for members who may not print or download
  onPrint?: () => void;
  onDownload?: () => void;
  pdfMaximize: boolean;
  setPdfMaximize: Dispatch<boolean>;
}

// Floats over the bottom of the document like the model viewer's toolbar:
// where you are on the left, how you look at it on the right.
export const PdfToolbar: FC<PdfToolbarProps> = ({
  pageNumber,
  pageCount,
  scale,
  sheetNumbers,
  pdfPointerMode,
  onPointerModeChange,
  onPrint,
  onDownload,
  pdfMaximize,
  setPdfMaximize,
}) => {
  // what is being typed into the page box, until Enter or leaving it
  const [pageDraft, setPageDraft] = useState(String(pageNumber));

  useEffect(() => {
    setPageDraft(String(pageNumber));
  }, [pageNumber]);

  // a page number, or a sheet number such as A-101
  const CommitPageDraft = () => {
    const requestedPage = /^\d+$/.test(pageDraft.trim())
      ? parseInt(pageDraft, 10)
      : Number(
          Object.keys(sheetNumbers).find(
            (sheetPage) =>
              NormalizeSheetNumber(sheetNumbers[Number(sheetPage)]) ===
              NormalizeSheetNumber(pageDraft),
          ) ?? NaN,
        );

    if (Number.isNaN(requestedPage)) {
      setPageDraft(String(pageNumber));
    } else {
      PdfHandler.GoToPage(requestedPage);
      setPageDraft(String(Math.max(1, Math.min(pageCount, requestedPage))));
    }
  };

  return (
    <div id="viewerToolbar">
      <ViewerToolbarButton
        imageSource="assets/icons/previous-page.png"
        title="Previous page"
        onClick={() => {
          PdfHandler.PreviousPage();
        }}
      />
      <div className="pdfToolbarPage">
        <input
          type="text"
          aria-label="Page or sheet number"
          title="Page or sheet number"
          value={pageDraft}
          onChange={(event) => {
            setPageDraft(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              CommitPageDraft();
              (event.target as HTMLInputElement).blur();
            } else if (event.key === "Escape") {
              setPageDraft(String(pageNumber));
              (event.target as HTMLInputElement).blur();
            }
          }}
          onBlur={CommitPageDraft}
        />
        <span>
          / {pageCount}
          {sheetNumbers[pageNumber] ? ` · ${sheetNumbers[pageNumber]}` : ""}
        </span>
      </div>
      <ViewerToolbarButton
        imageSource="assets/icons/next-page.png"
        title="Next page"
        onClick={() => {
          PdfHandler.NextPage();
        }}
      />
      <span className="viewerToolbarDivider" />
      <ViewerToolbarButton
        imageSource="assets/icons/zoom-out.png"
        title="Zoom out"
        onClick={() => {
          PdfHandler.ZoomOut();
        }}
      />
      <span className="pdfToolbarZoom">{Math.round(scale * 100)}%</span>
      <ViewerToolbarButton
        imageSource="assets/icons/zoom-in.png"
        title="Zoom in"
        onClick={() => {
          PdfHandler.ZoomIn();
        }}
      />
      <ViewerToolbarButton
        imageSource="assets/icons/fit-width.png"
        title="Fit to width"
        onClick={() => {
          PdfHandler.SetScaleMode("page-width");
        }}
      />
      <ViewerToolbarButton
        imageSource="assets/icons/fit-model.png"
        title="Fit page"
        onClick={() => {
          PdfHandler.SetScaleMode("page-fit");
        }}
      />
      <span className="viewerToolbarDivider" />
      <ViewerToolbarButton
        imageSource="assets/icons/rotate.png"
        title="Rotate clockwise"
        onClick={() => {
          PdfHandler.Rotate();
        }}
      />
      <ViewerToolbarButton
        imageSource={
          pdfPointerMode === "PAN"
            ? "assets/icons/pan.png"
            : "assets/icons/select-text.png"
        }
        title={
          pdfPointerMode === "PAN"
            ? "Pan (click to select text)"
            : "Select text (click to pan)"
        }
        onClick={() => {
          onPointerModeChange(pdfPointerMode === "PAN" ? "SELECT" : "PAN");
        }}
      />
      {onPrint || onDownload ? (
        <span className="viewerToolbarDivider" />
      ) : (
        <></>
      )}
      {onPrint ? (
        <ViewerToolbarButton
          imageSource="assets/icons/print.png"
          title="Print"
          onClick={onPrint}
        />
      ) : (
        <></>
      )}
      {onDownload ? (
        <ViewerToolbarButton
          imageSource="assets/icons/download-pdf.png"
          title="Download"
          onClick={onDownload}
        />
      ) : (
        <></>
      )}
      <span className="viewerToolbarDivider" />
      <ViewerToolbarButton
        imageSource={
          pdfMaximize
            ? "assets/icons/exit-full-screen.png"
            : "assets/icons/full-screen.png"
        }
        title={pdfMaximize ? "Exit full screen (Esc)" : "Full screen"}
        active={pdfMaximize}
        onClick={() => {
          setPdfMaximize(!pdfMaximize);
        }}
      />
    </div>
  );
};

export default PdfToolbar;
